import type { AuthUser } from '@/shared/types/auth';
import type { User, UserCredential } from 'firebase/auth';
import type { AuthTokenResult } from './types';

function toAuthUser(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    emailVerified: user.emailVerified,
    displayName: user.displayName,
    photoURL: user.photoURL,
  };
}

export class FirebaseAuthProvider {
  private unsubscribe: (() => void) | null = null;

  async initialize(onChange: (user: AuthUser | null) => void): Promise<void> {
    this.dispose();
    const [{ auth }, { onIdTokenChanged }] = await Promise.all([
      import('@/shared/lib/firebase'),
      import('firebase/auth'),
    ]);
    await new Promise<void>((resolve) => {
      let first = true;
      this.unsubscribe = onIdTokenChanged(auth, (user) => {
        onChange(user ? toAuthUser(user) : null);
        if (first) {
          first = false;
          resolve();
        }
      });
    });
  }

  dispose(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  async getAccessToken(): Promise<string | null> {
    const { auth } = await import('@/shared/lib/firebase');
    return auth.currentUser?.getIdToken(false) ?? null;
  }

  async getTokenResult(forceRefresh = false): Promise<AuthTokenResult | null> {
    const { auth } = await import('@/shared/lib/firebase');
    const result = await auth.currentUser?.getIdTokenResult(forceRefresh);
    if (!result) return null;
    return {
      token: result.token,
      claims: result.claims,
      authTime: result.authTime,
      signInProvider: result.signInProvider,
    };
  }

  async signOut(): Promise<void> {
    const [{ auth }, { signOut }] = await Promise.all([
      import('@/shared/lib/firebase'),
      import('firebase/auth'),
    ]);
    await signOut(auth);
  }

  async signInWithGoogle(): Promise<UserCredential> {
    const [{ auth }, { GoogleAuthProvider, signInWithPopup }] = await Promise.all([
      import('@/shared/lib/firebase'),
      import('firebase/auth'),
    ]);
    return signInWithPopup(auth, new GoogleAuthProvider());
  }

  async signInWithMicrosoft(): Promise<UserCredential> {
    const [{ auth }, { OAuthProvider, signInWithPopup }] = await Promise.all([
      import('@/shared/lib/firebase'),
      import('firebase/auth'),
    ]);
    const provider = new OAuthProvider('microsoft.com');
    // `tenant: 'common'` allows both work/school and personal Microsoft accounts; the prompt
    // prevents Microsoft from silently reusing an existing browser account.
    provider.setCustomParameters({ tenant: 'common', prompt: 'select_account' });
    return signInWithPopup(auth, provider);
  }

  async applyEmailVerificationCode(oobCode: string): Promise<void> {
    const [{ auth }, { applyActionCode }] = await Promise.all([
      import('@/shared/lib/firebase'),
      import('firebase/auth'),
    ]);
    await applyActionCode(auth, oobCode);
  }
}
